// Copyright ©️ 2026 eVolve MEP, LLC

import { useCallback, useState } from 'react';

import {
  useEvolvePost,
  getEntityAttachmentRoute,
  type UploadableEntityAttachmentType,
  type EntityAttachmentId,
  type EvolveApiBody,
} from '@evolvemep/api-ts';
import { MIME_TYPES } from '@mantine/dropzone';

import { uploadToPresignedUrl } from 'components/Mantine/FileUploads/uploadToPresignedUrl';
import { useMantineNotifications } from 'components/Mantine/useMantineNotifications';
import { isNil, isNotNil } from 'helpers/isNotNil';

export const FILE_TYPE_ACCEPT = [
  MIME_TYPES.xls,
  MIME_TYPES.xlsx,
  MIME_TYPES.csv,
  MIME_TYPES.pdf,
  MIME_TYPES.jpeg,
  MIME_TYPES.png,
];

export const useUploadFiles = <T extends UploadableEntityAttachmentType>(
  entityType: T,
  entityId: EntityAttachmentId<T>,
) => {
  const { showError } = useMantineNotifications();
  const { apiCall: uploadAttachmentApiCall } = useEvolvePost(
    getEntityAttachmentRoute(entityType, entityId, 'fileAttachment'),
  );
  const [saving, setSaving] = useState(false);

  const uploadFiles = useCallback(
    async (files: File[]) => {
      setSaving(true);
      try {
        const body: EvolveApiBody<
          'POST',
          ReturnType<typeof getEntityAttachmentRoute<UploadableEntityAttachmentType, 'fileAttachment'>>
        > = {
          files: files.map((f) => ({
            fileName: f.name,
          })),
        };
        const res = await uploadAttachmentApiCall(
          // I can't make this type happy, as the call to `apiCall` is becoming too type-expanded
          // oxlint-disable-next-line typescript/no-explicit-any
          ...([body] as any),
        );
        const responses = await Promise.allSettled(
          files.map(async (file) => {
            const presignedObj = res.find((f) => f.file.fileName === file.name);
            if (!presignedObj) {
              showError(`Failed to upload ${file.name}.`);
              return null;
            }
            const {
              file: { preSignedUrl, contentType, error },
            } = presignedObj;
            if (isNil(preSignedUrl)) {
              showError(`Failed to upload ${file.name}: ${error}`);
              return null;
            }
            await uploadToPresignedUrl(file, preSignedUrl, contentType);
            return presignedObj.file.fileId;
          }),
        );
        return responses.map((r) => (r.status === 'fulfilled' ? r.value : null)).filter(isNotNil);
      } finally {
        setSaving(false);
      }
    },
    [uploadAttachmentApiCall, showError],
  );

  return { uploadFiles, saving };
};
